<template>
  <div id="polygon-map"
  :style="style">
  </div>
</template>

<script>
import * as atlas from 'azure-maps-control'
import * as atlasDrawing from 'azure-maps-drawing-tools'
import { mapGetters, mapMutations } from 'vuex'
import {
  mapResizeTimeout, defaultCoordinates, azureMapsApiKey, createPolygonLayersForDataSource,
  PolygonStyle, zoomToFeature, disposeMap
} from '@/utils/map/MapHelpers'
import {
  dragHandleStyles, PolygonStatus, DrawingMode,
  azureMapsDrawingEvents as drawingEvents, MapDrawingAlert, MAX_VERTICES
} from '@/utils/map/MapDrawingManagerHelpers'
import { kebabCase, remove } from 'lodash'
import * as turf from '@turf/turf'
import { theme } from '@/plugins/theme'
import { GeojsonGeometryType, AngelGeojson, AngelGeojsonLayer } from '@/utils/map/Geojson'
import {
  shapeIsNot, flattenedCoordinatesFromShape, intersectsSelf,
  overlaps, tooComplex, simplifyShape, getAcresFromGeojson
} from '@/utils/map/GeospatialHelpers'

const CUT = AngelGeojsonLayer.Cut
const PARCELS = AngelGeojsonLayer.Parcels

export default {
  name: 'PolygonMap',

  props: {
    tract: {
      type: Object,
      required: true
    },
    containerId: {
      type: String,
      required: true
    },
    hintsEnabled: {
      type: Boolean,
      required: true
    },
    angelGeojson: {
      type: AngelGeojson,
      required: false,
      default: undefined
    },
    propShowMarker: {
      type: Boolean,
      required: true
    },
    propShowParcel: {
      type: Boolean,
      required: true
    },
    zoomToCutTrigger: {
      type: Boolean,
      required: true
    },
    simplifyTrigger: {
      type: Boolean,
      required: true
    },
    deselectTrigger: {
      type: Boolean,
      required: true
    },
    currentDrawingMode: {
      type: Object,
      required: true
    },
    currentEditModeOption: {
      type: String,
      required: true
    }
  },

  mounted () {
    this.setAllowEscapeToClose(false)
    this.$nextTick(_ => {
      this.initMap()
    })
  },

  beforeDestroy () {
    if (this.drawingManager) this.drawingManager.dispose()
    this.drawingManager = null
    if (this.polygonMap) disposeMap(this.polygonMap)
    this.polygonMap = null
    this.setAllowEscapeToClose(true)
  },

  data: () => ({
    MAX_VERTICES,
    DrawingMode,
    apiKey: azureMapsApiKey(),
    polygonMap: null,
    drawingManager: null,
    focusedShape: null,
    marker: null,
    hasInvalidPolygon: false,
    hasPolygonTooComplexToEdit: false,
    doesNotContainTractMarker: false,
    parcelSource: null,
    parcelLayers: null
  }),

  computed: {
    ...mapGetters('dialog', ['topDialogState']),
    style () {
      return {
        height: `max(calc(100vh - ${this.isFullscreen ? '120px' : '20vh - 23px'}), 300px)`,
        width: '100%',
        backgroundColor: 'rgba(0, 0, 0, 0.12)'
      }
    },

    isFullscreen () {
      return this.$vuetify.breakpoint.mdAndDown || this.topDialogState?.fullscreen
    },

    saveDisabled () {
      if (!this.drawingManager) return true
      const shapes = this.drawingManager.getSource().getShapes()

      return !!this.focusedShape ||
      this.hasInvalidPolygon ||
      this.doesNotContainTractMarker ||
      shapes.length < 1 ||
      shapes.some(s => !['Polygon', 'MultiPolygon'].includes(s.data.geometry.type)) // Can't save while a polygon is unfinished
    },

    hasShapes () {
      return !!this.shapes && this.shapes.filter(s => ['Polygon', 'MultiPolygon'].includes(s.getType())).length > 0
    },

    shapes () {
      return this.drawingManager?.getSource().getShapes()
    }
  },

  watch: {
    angelGeojson: {
      handler (geojson) {
        if (geojson.layerIsNotEmpty(PARCELS) && this.parcelSource) {
          this.parcelSource.setShapes(this.angelGeojson.getLayer(PARCELS))
          this.autoZoom()
        }
      },
      deep: true
    },

    currentDrawingMode (val, oldVal) {
      if (val?.id !== oldVal?.id) {
        this.handleDrawingModeChange(val)
      }
    },

    currentEditModeOption (val, oldVal) {
      if (val !== oldVal) {
        this.handleEditOptionChange(val)
      }
    },

    saveDisabled (val) {
      this.$emit('save-button-disabled', val)
    },

    propShowMarker (isVisible) {
      this.marker.setOptions({
        ...this.marker.getOptions(),
        color: isVisible ? theme.primary : 'none',
        secondaryColor: isVisible ? 'white' : 'none'
      })
    },

    propShowParcel (isVisible) {
      if (this.parcelLayers) {
        this.parcelLayers.forEach(layer => { this.setLayerVisibility(layer, isVisible) })
      }
    },

    hasInvalidPolygon (val) {
      if (val) {
        if ([DrawingMode.DrawPolygon, DrawingMode.DrawRectangle].includes(this.currentDrawingMode.id)) {
          this.setToIdle()
        }
      }
    },

    zoomToCutTrigger (val) {
      if (val) {
        this.autoZoom()
      }
    },

    simplifyTrigger (val) {
      if (val) {
        this.hasPolygonTooComplexToEdit.forEach(polygon => {
          this.simplify(polygon)
        })
      }
    },

    deselectTrigger (val) {
      if (val) {
        this.deselect()
      }
    },

    'drawingManager.editHelper.primaryHandles': {
      handler (handles) {
        handles.forEach(h => {
          this.polygonMap.events.add('mousedown', h, () => this.onPrimaryHandleClicked(h))
        })
      }
    },

    '$vuetify.breakpoint.height' () { this.onResize() },
    '$vuetify.breakpoint.width' () { this.onResize() },
    'topDialogState.fullscreen' () { this.onResize() }
  },

  methods: {
    kebabCase,
    ...mapMutations('dialog', ['setAllowEscapeToClose']),

    onResize () {
      if (this.polygonMap) {
        setTimeout(() => this.polygonMap.map.resize(), mapResizeTimeout)
      }
    },

    autoZoom () {
      const features = [new atlas.data.Point(new atlas.data.Position(this.tract.spot.longitude, this.tract.spot.latitude))]
      const dMgrShapes = this.drawingManager.getSource().getShapes()
      if (dMgrShapes) {
        features.push(dMgrShapes)
      }
      if (this.angelGeojson.layerIsNotEmpty(PARCELS)) {
        features.push(this.angelGeojson.getFeatures(PARCELS))
      }
      zoomToFeature(new atlas.data.FeatureCollection(features), this.polygonMap)
    },

    setLayerVisibility (layer, isVisible) {
      const options = layer.getOptions()
      options.visible = isVisible
      layer.setOptions(options)
    },

    initMap () {
      const mapOptions = {
        center: [this.tract.spot?.longitude ?? defaultCoordinates.longitude, this.tract.spot?.latitude ?? defaultCoordinates.latitude],
        zoom: 13,
        view: 'Auto',
        style: 'satellite_road_labels',
        authOptions: { authType: 'subscriptionKey', subscriptionKey: azureMapsApiKey() }
      }

      this.polygonMap = new atlas.Map('polygon-map', mapOptions)
      this.polygonMap.events.add('ready', this.onMapReady)
    },

    onMapReady () {
      this.initMarker()
      this.initParcel()
      this.initDrawingMgr()
      this.initDragHandleStyles()
      this.initDrawingMgrLayerStyles()
      this.initCut()
      this.autoZoom()
      this.$emit('map-ready', this.polygonMap)
    },

    initMarker () {
      this.marker = new atlas.HtmlMarker({
        draggable: false,
        position: [this.tract.spot.longitude, this.tract.spot.latitude],
        pixelOffset: [0, 0],
        color: theme.primary
      })
      this.polygonMap.markers.add(this.marker)
    },

    initParcel () {
      this.parcelSource = new atlas.source.DataSource()
      if (this.angelGeojson.layerIsNotEmpty(PARCELS)) {
        this.parcelSource.setShapes(this.angelGeojson.getLayer(PARCELS))
      }
      this.polygonMap.sources.add(this.parcelSource)
      this.parcelLayers = createPolygonLayersForDataSource(this.parcelSource, PolygonStyle.Parcels)
      this.polygonMap.layers.add(this.parcelLayers, 'labels')
    },

    initDrawingMgr () {
      const drawingManagerOptions = {
        mode: 'idle', // default
        interactionType: 'click',
        shapeDraggingEnabled: false,
        shapeRotationEnabled: false
      }

      this.drawingManager = new atlasDrawing.drawing.DrawingManager(this.polygonMap, drawingManagerOptions)
      this.drawingManagerLayers().forEach(layer => this.polygonMap.layers.move(layer, 'labels'))
      this.setDrawingEventListeners()
      this.$emit('drawing-manager-initialized', this.drawingManager)
    },

    drawingManagerLayers () {
      return Object.values(this.drawingManager.getLayers()).concat(Object.values(this.drawingManager.getPreviewLayers()))
    },

    removeMapMarker () { // Marker options includes a "visible" field, but it doesn't work for some reason, so we're using colors
      const options = this.marker.getOptions()
      options.color = 'none'
      options.secondaryColor = 'none'
      this.marker.setOptions(options)
    },

    initCut () {
      const source = this.drawingManager.getSource()
      if (!this.angelGeojson.layerIsNotEmpty(CUT)) return
      const cutLayer = this.angelGeojson.getLayer(CUT)
      const expandedFeatures = []
      cutLayer.features.forEach(feature => {
        if (feature.geometry.type === 'MultiPolygon') {
          feature.geometry.coordinates.forEach(polygonCoords => {
            expandedFeatures.push(new atlas.data.Feature(new atlas.data.Polygon(polygonCoords), feature.properties))
          })
        } else {
          expandedFeatures.push(feature)
        }
      })
      source.add(new atlas.data.FeatureCollection(expandedFeatures))
    },

    initDragHandleStyles () {
      this.drawingManager.setOptions(dragHandleStyles.default)
    },

    initDrawingMgrLayerStyles () {
      const getStrokeColor = (defaultColor) => { return ['case', ['has', 'strokeColor'], ['get', 'strokeColor'], defaultColor] }
      const getFillColor = (defaultColor) => { return ['case', ['has', 'fillColor'], ['get', 'fillColor'], defaultColor] }
      this.drawingManagerLayers().forEach(layer => {
        layer.setOptions({
          strokeColor: getStrokeColor(PolygonStatus.None.stroke),
          strokeWidth: 2,
          fillColor: getFillColor(PolygonStatus.None.fill),
          fillOpacity: 0.25
        })
      })
    },

    setDrawingEventListeners () {
      drawingEvents.forEach(e => {
        this.polygonMap.events.add(e,
          this.drawingManager, (event) => { this.onDrawingEvent(event, e) }
        )
      })
    },

    setToIdle () {
      this.drawingManager.setOptions({ mode: 'idle' })
    },

    setToErase () {
      this.drawingManager.setOptions({ mode: 'erase-geometry' })
    },

    setToEdit () {
      this.drawingManager.setOptions({ mode: 'edit-geometry' })
    },

    onDrawingEvent (eventContent, eventLabel) {
      switch (eventLabel) {
        case 'drawingstarted': {
          if (this.currentDrawingMode.azMapsLabel === 'edit-geometry') {
            this.setShapeColorForAll(PolygonStatus.None)
            this.focusedShape = eventContent
            if (eventContent) this.setShapeColor(eventContent, PolygonStatus.Focused)
          }
          break
        }
        case 'drawingmodechanged': {
          this.ensureValidPolygons()
          const changed = this.currentDrawingMode.azMapsLabel !== eventContent
          if (changed) {
            this.$emit('drawing-mode-changed', Object.values(DrawingMode).find(dm => dm.azMapsLabel === eventContent))
          }
          break
        }
        case 'drawingcomplete': {
          this.ensureValidPolygons()
          this.calculateAcres()
          this.focusedShape = null
          break
        }
        case 'drawingerased': {
          this.$nextTick(_ => {
            if (this.hasShapes) this.setToErase() // We want erase mode to stay active until the user switches modes.
            this.ensureValidPolygons()
            this.calculateAcres()
            this.focusedShape = null
          })
          break
        }
      }
    },

    onPrimaryHandleClicked (handle) {
      if (this.currentEditModeOption === 'eraseVertices') {
        this.eraseVertex(handle)
      }
    },

    eraseVertex (handle) {
      const position = handle.options.position
      const shape = this.focusedShape ?? this.drawingManager.editHelper.shape
      const coords = shape.data.geometry.coordinates[0]
      const coordsLengthOld = coords.length
      remove(coords, coord => coord[0] === position[0] && coord[1] === position[1])
      const coordsLengthNew = coords.length
      const anchorVertexDeleted = coordsLengthOld - coordsLengthNew === 2 // Final coordinate must be duplicate of first coordinate to render correctly
      if (anchorVertexDeleted) coords.push([...coords[0]])
      this.$nextTick(_ => {
        this.drawingManager.edit(shape)
      })
    },

    setShapeColor (shape, status) {
      shape.setProperties({ status: status.id, strokeColor: status.stroke, fillColor: status.fill })
    },

    setShapeColorForAll (status) {
      const shapes = this.drawingManager.source.getShapes()
      if (shapes) {
        shapes.forEach(s => this.setShapeColor(s, status))
        this.ensureValidPolygons() // Keep invalid shapes invalid
      }
    },

    ensureValidPolygons () {
      if (this.hasShapes) {
        this.ensureNoPointsOrLines()
        this.ensureNoRectangleProperty()
        if (this.hasShapesOverMaxVertices()) {
          this.hasPolygonTooComplexToEdit = this.shapes.filter(s => tooComplex(s, MAX_VERTICES))
          this.$emit('alert', MapDrawingAlert.TooComplex)
          return
        } else if (this.hasSelfIntersection()) {
          this.hasInvalidPolygon = true
          this.$emit('alert', MapDrawingAlert.IntersectSelf)
          return
        } else if (this.hasOverlap()) {
          this.hasInvalidPolygon = true
          this.$emit('alert', MapDrawingAlert.Overlap)
          return
        } else if (this.doesNotContainMarker()) {
          this.doesNotContainTractMarker = true
          this.$emit('alert', MapDrawingAlert.MarkerOutside)
          return
        }
      }

      this.hasPolygonTooComplexToEdit = false
      this.doesNotContainTractMarker = false
      this.hasInvalidPolygon = false
      this.$emit('alert', MapDrawingAlert.None)
    },

    ensureNoPointsOrLines () {
      this.shapes.forEach(s => this.handleTooFewVertices(s))
    },

    handleTooFewVertices (shape) {
      const mode = this.currentDrawingMode
      const shapeStillInProgress = shapeIsNot(shape, GeojsonGeometryType.Polygon) && mode.id === DrawingMode.DrawPolygon.id
      if (shapeStillInProgress) return

      const src = this.drawingManager.getSource()
      const coordinatesAsString = flattenedCoordinatesFromShape(shape).map(([lon, lat]) => `${lon},${lat}`)
      const uniqueCoordCount = [...new Set(coordinatesAsString)].length // Polygons duplicate their first coord as their last (for edge rendering)
      if (uniqueCoordCount < 3) {
        const id = shape.getId()
        src.removeById(id)
        return true
      }
      return false
    },

    ensureNoRectangleProperty () {
      this.shapes.forEach(s => this.removeRectangleProperty(s))
    },

    removeRectangleProperty (shape) {
      let removedRectangleProperty = false
      const props = shape.getProperties()
      if (props.subType === 'Rectangle') {
        delete props.subType // Azure Maps prevents line splitting on rectangles. We want rectangles to be treated as normal polygons
        shape.setProperties(props)
        removedRectangleProperty = true
      }
      return removedRectangleProperty
    },

    hasSelfIntersection () {
      let selfIntersection = false
      this.shapes.forEach((s) => {
        if (intersectsSelf(s)) {
          this.setShapeColor(s, PolygonStatus.Invalid)
          selfIntersection = true
        }
      })
      return selfIntersection
    },

    hasOverlap () {
      let overlap = false
      this.shapes.forEach((s, _, shapes) => {
        if (overlaps(s, shapes)) {
          this.setShapeColor(s, PolygonStatus.Invalid)
          overlap = true
        }
      })
      return overlap
    },

    hasShapesOverMaxVertices () {
      return this.shapes.some(s => tooComplex(s, MAX_VERTICES))
    },

    doesNotContainMarker () {
      return !this.shapes.some(s => {
        const geom = s.data.geometry
        if (geom.type === 'MultiPolygon') {
          return geom.coordinates.some(polygonCoords => turf.booleanContains(turf.polygon(polygonCoords), turf.point([this.tract.spot.longitude, this.tract.spot.latitude])))
        }
        return turf.booleanContains(turf.polygon(geom.coordinates), turf.point([this.tract.spot.longitude, this.tract.spot.latitude]))
      })
    },

    simplify (shape) {
      const TOLERANCE = 0.00001
      simplifyShape(shape, TOLERANCE, MAX_VERTICES)
      this.setSnack(this.$t('coordCountReduced'))
      this.setToEdit()
    },

    handleDrawingModeChange (mode) {
      this.drawingManager.setOptions({ mode: mode.azMapsLabel })
      switch (mode.azMapsLabel) {
        case 'edit-geometry': {
          this.$nextTick(_ => { this.setShapeColorForAll(PolygonStatus.Focusable) })
          break
        }
        case 'erase-geometry':
          this.$nextTick(_ => {
            this.setShapeColorForAll(PolygonStatus.Erasable)
          })
          break
        default:
          this.$nextTick(_ => {
            this.setShapeColorForAll(PolygonStatus.None)
          })
      }
    },

    handleEditOptionChange (option) {
      const persistFocusedShape = option !== 'matchParcel'
      if (option === 'matchParcel') {
        this.handleMatchingParcel()
        return
      }
      const existingFocusedShape = this.focusedShape
      let options = {
        mode: 'edit-geometry',
        shapeDraggingEnabled: false,
        shapeRotationEnabled: false,
        ...dragHandleStyles.default
      }
      switch (option) {
        case 'dragVertices': break
        case 'eraseVertices':
          options = { ...options, ...dragHandleStyles.deletion }
          break
        case 'dragAndRotate':
          options.shapeDraggingEnabled = true
          options.shapeRotationEnabled = true
          options = { ...options, ...dragHandleStyles.none }
      }
      this.drawingManager.setOptions(options)
      if (existingFocusedShape && persistFocusedShape) {
        this.drawingManager.edit(existingFocusedShape)
      } else {
        this.$nextTick(_ => { this.setShapeColorForAll(PolygonStatus.Focusable) })
      }
    },

    handleMatchingParcel () {
      const src = this.drawingManager.getSource()
      const type = this.parcelSource.getShapes()[0].getType()
      const polygons = this.parcelSource.shapes[0].data.geometry.coordinates.map(coords => {
        return new atlas.data.Polygon(
          type === 'MultiPolygon' ? coords : [coords]
        )
      })
      const featureCollection = new atlas.data.FeatureCollection(polygons)
      src.setShapes(featureCollection)
      this.setToIdle()
      this.ensureValidPolygons()
    },

    deselect () {
      let options = {
        mode: 'edit-geometry',
        shapeDraggingEnabled: false,
        shapeRotationEnabled: false,
        ...dragHandleStyles.default
      }
      switch (this.currentEditModeOption) {
        case 'dragVertices': break
        case 'eraseVertices':
          options = { ...options, ...dragHandleStyles.deletion }
          break
        case 'dragAndRotate':
          options.shapeDraggingEnabled = true
          options.shapeRotationEnabled = true
          options = { ...options, ...dragHandleStyles.none }
      }
      this.drawingManager.setOptions(options)
      this.$nextTick(_ => { this.setShapeColorForAll(PolygonStatus.Focusable) })
    },

    calculateAcres () {
      this.$emit('acres', getAcresFromGeojson(this.drawingManager.getSource().toJson()))
    }
  }
}
</script>

<style src="@/styles/map-scoped.css"/>
<style src="@/styles/map-global.css"/>
<style src="@/styles/atlas-drawing.css"/>
